import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchLeaderboardHistory, LeaderboardHistoryData, RoundResult } from '../../lib/adminApi';
import './LeaderboardPage.css';

interface TeamRow {
    username: string;
    roundPnls: number[];
    roundScores: number[];
    cumulative: number;
    cumulativeScore: number;
    rankAfterRound: number[];
    isBot: boolean;
    /** Cumulative rank (1 = best); same rank when score and PnL tie. 0 for bots. */
    displayRank: number;
}

function buildRows(data: LeaderboardHistoryData): { rounds: RoundResult[]; rows: TeamRow[] } {
    const { rounds } = data;
    const roundPnlsMap = new Map<string, number[]>();
    const roundScoresMap = new Map<string, number[]>();
    const botSet = new Set<string>();

    rounds.forEach((round, idx) => {
        for (const entry of round.results) {
            if (!roundPnlsMap.has(entry.username)) {
                roundPnlsMap.set(entry.username, new Array(rounds.length).fill(0));
                roundScoresMap.set(entry.username, new Array(rounds.length).fill(0));
            }
            roundPnlsMap.get(entry.username)![idx] = entry.pnl;
            roundScoresMap.get(entry.username)![idx] = entry.score ?? 0;
            if (entry.bot) botSet.add(entry.username);
        }
    });

    const allRows: TeamRow[] = [];
    roundPnlsMap.forEach((pnls, username) => {
        const scores = roundScoresMap.get(username)!;
        allRows.push({
            username,
            roundPnls: pnls,
            roundScores: scores,
            cumulative: pnls.reduce((a, b) => a + b, 0),
            cumulativeScore: scores.reduce((a, b) => a + b, 0),
            rankAfterRound: [],
            isBot: botSet.has(username),
            displayRank: 0,
        });
    });

    const teamRows = allRows.filter((r) => !r.isBot);
    const botRows = allRows.filter((r) => r.isBot);

    // Teams: lower cumulativeScore is better; tie-break by higher cumulative PnL.
    teamRows.sort((a, b) => a.cumulativeScore - b.cumulativeScore || b.cumulative - a.cumulative);

    // Cumulative display rank: same rank when (cumulativeScore, cumulative) are equal.
    for (let i = 0; i < teamRows.length; i++) {
        const prev = teamRows[i - 1];
        const tied =
            i > 0 &&
            prev &&
            prev.cumulativeScore === teamRows[i].cumulativeScore &&
            prev.cumulative === teamRows[i].cumulative;
        teamRows[i].displayRank = tied ? prev.displayRank : i + 1;
    }

    // Bots: sort by cumulative PnL (desc) for display at bottom.
    botRows.sort((a, b) => b.cumulative - a.cumulative);

    // Rank-after-round only for teams; same (cumScore, cumPnl) → same rank.
    for (let j = 0; j < rounds.length; j++) {
        const cumScores = teamRows.map((r) =>
            r.roundScores.slice(0, j + 1).reduce((a, b) => a + b, 0),
        );
        const cumPnls = teamRows.map((r) => r.roundPnls.slice(0, j + 1).reduce((a, b) => a + b, 0));
        const order = teamRows
            .map((_, i) => i)
            .sort((a, b) => cumScores[a] - cumScores[b] || cumPnls[b] - cumPnls[a]);
        let nextRank = 1;
        for (let k = 0; k < order.length; k++) {
            const idx = order[k];
            const tied =
                k > 0 &&
                cumScores[order[k - 1]] === cumScores[idx] &&
                cumPnls[order[k - 1]] === cumPnls[idx];
            teamRows[idx].rankAfterRound[j] = tied
                ? teamRows[order[k - 1]].rankAfterRound[j]
                : nextRank;
            if (!tied) nextRank = k + 2;
        }
        botRows.forEach((r) => (r.rankAfterRound[j] = 0));
    }

    return { rounds, rows: [...teamRows, ...botRows] };
}

function rankClass(index: number): string {
    if (index === 0) return 'rank-gold';
    if (index === 1) return 'rank-silver';
    if (index === 2) return 'rank-bronze';
    if (index >= 3 && index < 10) return 'rank-4-10';
    if (index >= 10 && index < 20) return 'rank-11-20';
    if (index >= 20 && index < 30) return 'rank-21-30';
    if (index >= 30) return 'rank-31-plus';
    return '';
}

function formatPnl(value: number): string {
    if (value === 0) return '0';
    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toLocaleString()}`;
}

function formatPnlDollar(value: number): string {
    if (value === 0) return '$0.00';
    const sign = value > 0 ? '+' : '-';
    return `${sign}$${Math.abs(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatScore(value: number): string {
    return Math.round(value).toString();
}

const PODIUM_COLORS = [
    '#c0c0c0',
    '#ffd700',
    '#cd7f32',
]; /* silver, gold, bronze for 2nd, 1st, 3rd */

/** Rank-over-rounds chart: rank 1 at top, each point labeled with rank. */
function RankChart({
    rankAfterRound,
    stroke,
    className,
}: {
    rankAfterRound: number[];
    stroke: string;
    className?: string;
}) {
    if (rankAfterRound.length === 0)
        return <div className={`podium-rank-chart ${className || ''}`} />;
    const maxRank = Math.max(...rankAfterRound, 1);
    const w = 400;
    const h = 88;
    const padding = { top: 16, right: 14, bottom: 14, left: 14 };
    const innerW = w - padding.left - padding.right;
    const xScale = (i: number) =>
        padding.left +
        (rankAfterRound.length <= 1 ? innerW / 2 : (i / (rankAfterRound.length - 1)) * innerW);
    // Invert so rank 1 is at top: y smaller = rank better
    const yScale = (rank: number) =>
        h -
        padding.bottom -
        ((maxRank + 1 - rank) / (maxRank || 1)) * (h - padding.top - padding.bottom);
    const points = rankAfterRound.map((rank, i) => `${xScale(i)},${yScale(rank)}`).join(' ');
    return (
        <svg
            className={`podium-rank-chart ${className || ''}`}
            viewBox={`0 0 ${w} ${h}`}
            preserveAspectRatio="xMidYMid meet"
        >
            <polyline
                fill="none"
                stroke={stroke}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                points={points}
            />
            {rankAfterRound.map((rank, i) => {
                const cx = xScale(i);
                const cy = yScale(rank);
                const r = 10;
                return (
                    <g key={i}>
                        <circle
                            cx={cx}
                            cy={cy}
                            r={r}
                            fill={stroke}
                            stroke="#fff"
                            strokeWidth="1.5"
                        />
                        <text
                            x={cx}
                            y={cy}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fontSize="10"
                            fill="#000"
                            fontWeight="bold"
                        >
                            {rank}
                        </text>
                    </g>
                );
            })}
        </svg>
    );
}

const LeaderboardPage = () => {
    const [data, setData] = useState<LeaderboardHistoryData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const refresh = useCallback(async () => {
        setLoading(true);
        setError('');
        const result = await fetchLeaderboardHistory();
        if (result) {
            setData(result);
        } else {
            setError('Could not reach the server.');
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const hasData = data && data.rounds.length > 0;
    const { rounds, rows } = hasData ? buildRows(data) : { rounds: [], rows: [] };
    const teamRows = rows.filter((r) => !r.isBot);

    return (
        <div className="leaderboard-page">
            <div className="leaderboard-header">
                <div>
                    <h1>Leaderboard</h1>
                    <p className="leaderboard-subtitle">
                        Round score and PnL; cumulative score and PnL. Ranked by cumulative score.
                        Public — no login required.
                    </p>
                </div>
                <div className="leaderboard-actions">
                    <button className="lb-btn" onClick={refresh} disabled={loading}>
                        {loading ? 'Refreshing...' : 'Refresh'}
                    </button>
                    <Link to="/" className="lb-link">
                        Trader Login
                    </Link>
                    <Link to="/admin" className="lb-link">
                        Admin Panel
                    </Link>
                </div>
            </div>

            {error && <div className="lb-error">{error}</div>}

            {!loading && !error && !hasData && (
                <div className="lb-empty">No rounds have been recorded yet.</div>
            )}

            {hasData && teamRows.length >= 3 && (
                <div className="leaderboard-podium">
                    {[teamRows[1], teamRows[0], teamRows[2]].map((row, podiumIndex) => {
                        const rank = podiumIndex === 0 ? 2 : podiumIndex === 1 ? 1 : 3;
                        const borderColor = PODIUM_COLORS[podiumIndex];
                        return (
                            <div
                                key={row.username}
                                className={`podium-column ${rank === 1 ? 'podium-first' : ''}`}
                            >
                                <div
                                    className="podium-avatar"
                                    style={{ borderColor }}
                                    title={row.username}
                                >
                                    <span className="podium-avatar-number">{rank}</span>
                                </div>
                                <div className="podium-name">{row.username}</div>
                                <div className="podium-card">
                                    <div className="podium-card-label">Cumulative score</div>
                                    <div className="podium-score-value">
                                        {formatScore(row.cumulativeScore)}
                                    </div>
                                    <div className="podium-card-label">Realized P&L</div>
                                    <div
                                        className={`podium-pnl-value ${row.cumulative >= 0 ? 'pnl-pos' : 'pnl-neg'}`}
                                    >
                                        {formatPnlDollar(row.cumulative)}
                                    </div>
                                    <div className="podium-chart-wrap">
                                        <span className="podium-chart-title">Rank over rounds</span>
                                        <RankChart
                                            rankAfterRound={row.rankAfterRound}
                                            stroke={borderColor}
                                        />
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {hasData && (
                <div className="lb-table-wrap">
                    <table className="lb-table">
                        <thead>
                            <tr>
                                <th className="col-rank">#</th>
                                <th className="col-team">Team</th>
                                {rounds.map((r, i) => (
                                    <th key={i} className="col-round">
                                        {r.roundName}
                                    </th>
                                ))}
                                <th className="col-cumulative">Cumulative</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, idx) => {
                                const isFirstBot = row.isBot && (idx === 0 || !rows[idx - 1].isBot);
                                return (
                                    <React.Fragment key={row.username}>
                                        {isFirstBot && (
                                            <tr className="lb-bot-sep">
                                                <td colSpan={2 + rounds.length + 1}>
                                                    Trading bots
                                                </td>
                                            </tr>
                                        )}
                                        <tr
                                            className={
                                                row.isBot
                                                    ? 'lb-bot-row'
                                                    : rankClass(row.displayRank - 1)
                                            }
                                        >
                                            <td className="col-rank">
                                                {row.isBot ? '—' : row.displayRank}
                                            </td>
                                            <td className="col-team">{row.username}</td>
                                            {row.roundPnls.map((pnl, i) => (
                                                <td
                                                    key={i}
                                                    className={`col-round ${pnl > 0 ? 'pnl-pos' : pnl < 0 ? 'pnl-neg' : ''}`}
                                                >
                                                    <div className="round-score">
                                                        {row.isBot
                                                            ? '—'
                                                            : formatScore(row.roundScores[i])}
                                                    </div>
                                                    <div>{formatPnl(pnl)}</div>
                                                </td>
                                            ))}
                                            <td
                                                className={`col-cumulative ${row.cumulative > 0 ? 'pnl-pos' : row.cumulative < 0 ? 'pnl-neg' : ''}`}
                                            >
                                                <div className="round-score">
                                                    {row.isBot
                                                        ? '—'
                                                        : formatScore(row.cumulativeScore)}
                                                </div>
                                                <div>{formatPnl(row.cumulative)}</div>
                                            </td>
                                        </tr>
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default LeaderboardPage;
